import React from 'react';
import { X, Car, Shield, AlertCircle } from 'lucide-react';
import { BidHistoryGraph } from './shared/BidHistoryGraph';

interface RequestDetailsModalProps {
  request: any;
  onClose: () => void;
  onCancel: () => Promise<void>;
  isLoading: boolean;
}

export function RequestDetailsModal({ request, onClose, onCancel, isLoading }: RequestDetailsModalProps) {
  const isExpired = new Date(request.expires_at) < new Date();
  const activeBids = request.bids?.filter(b => b.status === 'active') || [];
  const hasActiveBids = activeBids.length > 0;
  const age = request.age || calculateAge(request.date_of_birth);

  function calculateAge(dob: string): number {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Request Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Client Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Shield className="w-5 h-5 text-gray-500 mr-2" />
              Client Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Name</label>
                <p className="mt-1">{request.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Email</label>
                <p className="mt-1">{request.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Phone</label>
                <p className="mt-1">{request.phone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Age</label>
                <p className="mt-1">{age} years</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-500">Address</label>
                <p className="mt-1">{request.address}</p>
              </div>
            </div>
          </div>

          {/* Vehicle Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Car className="w-5 h-5 text-gray-500 mr-2" />
              Vehicle Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Make & Model</label>
                <p className="mt-1">{request.car_make} {request.car_model}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Registration</label>
                <p className="mt-1">{request.registration}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Year</label>
                <p className="mt-1">{request.car_year}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Mileage</label>
                <p className="mt-1">{request.mileage.toLocaleString()} miles</p>
              </div>
            </div>
          </div>

          {/* Insurance Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Shield className="w-5 h-5 text-gray-500 mr-2" />
              Insurance Details
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Coverage Type</label>
                <p className="mt-1 capitalize">{request.coverage_type.replace(/-/g, ' ')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">No Claims Discount</label>
                <p className="mt-1">
                  {request.no_claims_years} years
                  {request.protect_no_claims && ' (Protected)'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  request.bid_status === 'awaiting_bids' ? 'bg-blue-100 text-blue-800' :
                  request.bid_status === 'bidding_in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  request.bid_status === 'bidding_complete' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {request.bid_status === 'awaiting_bids' ? 'Awaiting Bids' :
                   request.bid_status === 'bidding_in_progress' ? 'Bidding in Progress' :
                   request.bid_status === 'bidding_complete' ? 'Bidding Complete' :
                   'No Bids'}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Created</label>
                <p className="mt-1">{new Date(request.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Bid History */}
          {request.bids && request.bids.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Bid History</h3>
              <BidHistoryGraph bids={request.bids} height={200} showAxis />
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Total Bids</label>
                    <p className="mt-1">{request.bids.length}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Active Bids</label>
                    <p className="mt-1">{activeBids.length}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Lowest Bid</label>
                    <p className="mt-1">
                      {request.lowest_bid 
                        ? `£${request.lowest_bid.toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}` 
                        : 'No active bids'}
                    </p>
                  </div>
                  {activeBids.length >= 2 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Potential Savings</label>
                      <p className="mt-1 text-green-600">
                        £{(Math.max(...activeBids.map(b => b.amount)) - Math.min(...activeBids.map(b => b.amount)))
                          .toLocaleString('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              Close
            </button>
            {!isExpired && (
              <button
                onClick={onCancel}
                disabled={isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Cancelling...
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Cancel Request
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}